// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import {
  Button,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from '@mui/material'
import { omit, pick, pull } from 'lodash'
import assign from 'lodash/assign'
import React from 'react'
import { Mutation } from 'react-apollo'
import { i18n } from '../../index'
import css from './checklistForm.css'
import {
  createChecklistItemMutation,
  createContinuingChecklistItemMutation,
  updateChecklistItemMutation,
  updateContinuingChecklistItemMutation,
} from './checklistQueries'
import { ContinuingChecklistInterval, type ChecklistItem, type ChecklistItemForm } from './checklistTypes'
import { checklistItemTypes } from './constants'
type ChecklistFormData = ChecklistItemForm
const defaultFormData = {
  id: null,
  description: '',
  type: [],
  shiftStatus: {
    morning: 'Available',
    evening: 'Available',
    night: 'Available',
  },
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  interval: '',
  isContinuing: false,
}
type Props = {
  data: ChecklistItem | null | undefined
  stationId?: string
  onSubmit: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
}
type State = {
  formData: ChecklistFormData
}

class ChecklistForm extends React.Component<Props, State> {
  state = {
    formData: { ...defaultFormData },
  }

  componentDidMount() {
    const { data } = this.props

    if (data) {
      this.setStateFromData(data)
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { data } = this.props

    if (data && JSON.stringify(data) !== JSON.stringify(prevProps.data)) {
      this.setStateFromData(data)
    }
  }

  setStateFromData = (data: ChecklistItem) => {
    const type = data.type || []
    this.setState({
      formData: { ...defaultFormData, ...data, type, isContinuing: !!data.interval },
    })
  }
  onInput = (fieldName: string, newValue: string | number) => {
    this.setState({
      formData: { ...this.state.formData, [fieldName]: newValue },
    })
  }
  onTypeChange = (newValue: string[] | number) => {
    this.setState({
      formData: { ...this.state.formData, type: newValue },
    })
  }
  onShiftStatusChange = (shift: string, newValue: boolean) => {
    this.setState({
      formData: {
        ...this.state.formData,
        shiftStatus: {
          ...this.state.formData.shiftStatus,
          [shift]: newValue ? 'Available' : 'NotAvailable',
        },
      },
    })
  }
  onWeekDayAvailabilityChange = (day: string, enabled: boolean) => {
    const prevDays = [...this.state.formData.days]
    pull(prevDays, day)
    const updatedDays = enabled ? [...prevDays, day] : prevDays
    this.setState({
      formData: { ...this.state.formData, days: updatedDays },
    })
  }
  onSubmit = async (onMutate: (...args: Array<any>) => any, e: any) => {
    e.preventDefault()
    const { stationId, refetch } = this.props
    const { onSubmit } = this.props
    const { formData } = this.state
    const checklistItem = this.continuingChecklistItemIsEnabled()
      ? assign({}, pick(formData, ['description', 'type', 'interval']), {
          stationId,
        })
      : assign({}, omit(formData, ['id', 'shiftStatus.__typename', 'station', 'interval', 'isContinuing']), {
          stationId,
        })
    //If empty string, set as null.
    checklistItem.type = checklistItem.type.length ? checklistItem.type : null
    await onMutate(
      formData.id
        ? {
            variables: {
              id: formData.id,
              checklistItem,
            },
          }
        : {
            variables: {
              checklistItem,
            },
          },
    )
    refetch()
    onSubmit()
    this.onReset()
  }
  onReset = () => {
    this.setState({
      formData: { ...defaultFormData },
    })
  }
  toggleContinuingChecklistItem = () => {
    const { formData } = this.state
    const isContinuing = formData.isContinuing ?? false
    this.setState({
      formData: { ...this.state.formData, isContinuing: !isContinuing, interval: '' },
    })
  }
  continuingChecklistItemIsEnabled = () => {
    const { formData } = this.state
    return formData.isContinuing === true
  }

  continuingChecklistIntervalChanged = (value: string) => {
    this.setState({
      formData: { ...this.state.formData, interval: value },
    })
  }

  render() {
    const { onCancel } = this.props
    const { formData } = this.state
    const isIncomplete = !formData.description.length
    let mutation

    if (this.continuingChecklistItemIsEnabled()) {
      mutation = formData.id ? updateContinuingChecklistItemMutation : createContinuingChecklistItemMutation
    } else {
      mutation = formData.id ? updateChecklistItemMutation : createChecklistItemMutation
    }

    return (
      <Mutation mutation={mutation}>
        {(onMutate) => (
          <form noValidate onSubmit={(e) => this.onSubmit(onMutate, e)} className={css.component}>
            <TextField
              autoFocus
              required
              label={i18n.t('admin.checklists.label.description')}
              value={formData.description}
              onChange={(e) => this.onInput('description', e.target.value)}
              className={css.textField}
            />
            <FormControl>
              <InputLabel id='typeSelectLabel'>{i18n.t('admin.checklists.label.type')}</InputLabel>
              <Select
                className={css.typeSelect}
                required
                value={formData.type}
                onChange={(e) => this.onTypeChange(e.target.value)}
                multiple
              >
                <MenuItem value=''>
                  <em>{i18n.t('admin.checklists.pickType')}</em>
                </MenuItem>
                {checklistItemTypes.map((type, i) => (
                  <MenuItem key={i} value={type}>
                    {i18n.t(`checkList.type.${type}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl component='fieldset'>
              <FormLabel component='legend'>{i18n.t('admin.checklists.legend.shiftStatus')}</FormLabel>
              <FormGroup className={css.formGroup}>
                <FormControlLabel
                  label={i18n.t('admin.checklists.label.continuingChecklist.continuingChecklist')}
                  control={
                    <Switch
                      checked={formData.isContinuing}
                      onChange={() => this.toggleContinuingChecklistItem()}
                      value='Monthly'
                      color='primary'
                      disabled={!!formData.id}
                    />
                  }
                />
                <RadioGroup
                  aria-label='ContinuingChecklist'
                  name='continuingChecklistInterval'
                  onChange={(e) => this.continuingChecklistIntervalChanged(e.target.value)}
                  className={css.radioGroup}
                >
                  <FormControlLabel
                    value={ContinuingChecklistInterval.Monthly}
                    control={<Radio checked={formData.interval === ContinuingChecklistInterval.Monthly} />}
                    label={i18n.t('admin.checklists.label.continuingChecklist.monthly')}
                    disabled={!this.continuingChecklistItemIsEnabled()}
                  />
                  <FormControlLabel
                    value={ContinuingChecklistInterval.Weekly}
                    control={<Radio checked={formData.interval === ContinuingChecklistInterval.Weekly} />}
                    label={i18n.t('admin.checklists.label.continuingChecklist.weekly')}
                    disabled={!this.continuingChecklistItemIsEnabled()}
                  />
                  <FormControlLabel
                    value={ContinuingChecklistInterval.Daily}
                    control={<Radio checked={formData.interval === ContinuingChecklistInterval.Daily} />}
                    label={i18n.t('admin.checklists.label.continuingChecklist.daily')}
                    disabled={!this.continuingChecklistItemIsEnabled()}
                  />
                </RadioGroup>
              </FormGroup>
              <FormGroup className={css.formGroup}>
                <FormControlLabel
                  label={i18n.t('admin.checklists.label.shiftStatus.morning')}
                  control={
                    <Switch
                      checked={formData.shiftStatus.morning === 'Available'}
                      onChange={(e) => this.onShiftStatusChange('morning', e.target.checked)}
                      value='morning'
                      color='primary'
                      disabled={this.continuingChecklistItemIsEnabled()}
                    />
                  }
                />
                <FormControlLabel
                  label={i18n.t('admin.checklists.label.shiftStatus.evening')}
                  control={
                    <Switch
                      checked={formData.shiftStatus.evening === 'Available'}
                      onChange={(e) => this.onShiftStatusChange('evening', e.target.checked)}
                      value='evening'
                      color='primary'
                      disabled={this.continuingChecklistItemIsEnabled()}
                    />
                  }
                />
                <FormControlLabel
                  label={i18n.t('admin.checklists.label.shiftStatus.night')}
                  control={
                    <Switch
                      checked={formData.shiftStatus.night === 'Available'}
                      onChange={(e) => this.onShiftStatusChange('night', e.target.checked)}
                      value='night'
                      color='primary'
                      disabled={this.continuingChecklistItemIsEnabled()}
                    />
                  }
                />
              </FormGroup>
              <FormLabel component='legend'>{i18n.t('admin.checklists.legend.weekDayAvailability')}</FormLabel>
              <FormGroup className={css.formGroup}>
                {defaultFormData.days.map((day) => (
                  <FormControlLabel
                    key={i18n.t(`admin.checklists.label.weekDayAvailability.${day.toLowerCase()}`)}
                    label={i18n.t(`admin.checklists.label.weekDayAvailability.${day.toLowerCase()}`)}
                    control={
                      <Switch
                        checked={formData.days.includes(day)}
                        onChange={(e) => this.onWeekDayAvailabilityChange(day, e.target.checked)}
                        value={day}
                        color='primary'
                        disabled={this.continuingChecklistItemIsEnabled()}
                      />
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
            <DialogActions className={css.actions}>
              <Button type='button' onClick={onCancel}>
                {i18n.t('buttons.cancel')}
              </Button>
              <Button type='submit' disabled={isIncomplete} color='primary'>
                {i18n.t('buttons.save')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Mutation>
    )
  }
}

export default ChecklistForm
